/*******************************************************
 * Copyright (C) 2010-Present Avant Assessment
 * All Rights Reserved
 *******************************************************/

import * as React from "react"
import MaterialTable, {Column} from "material-table"
import {reportsStore} from "../../../reports/common/stores/ReportStore"
import {AvantColors} from "../../../../styles/AvantColors"
import {ReportProps} from "../../ReportData"
import {useEffect, useRef, useState} from "react"
import {theme} from "../../../../styles/MuiThemes"
import {ReportTakeRow} from "../../../reports/class/models/ReportTakeRow"
import {PanelSkillEnum} from "../../../../types/types"
import testTakerSortableTableStore from "./TestTakerSortableTableStore"
import {takeSearchStore} from "../../../common/take/TakeSearchStore"
import {buildReportColumns, SkillColumnData} from "./Columns"
import {ProctorTypes} from "../../../../util/Constants"
import {sidebarStore} from "../testTakerSidebar/SideBarStore"
import {observer} from "mobx-react-lite"
import {MuiThemeProvider} from "@material-ui/core"

export interface PanelSkill {
    symbol: string,
    name: string
}

interface TestTakerSortableTableProps {
    reportProps: ReportProps
    isMultiLanguage: boolean
}

interface TableDataRow extends ReportTakeRow {
   tableData: {
       id: number,
       checked: boolean,
   }
}

export function openSidebarWithId(
    row: ReportTakeRow,
    skill?: PanelSkillEnum,
    selfEval?: boolean,
) {
    return () => {
        sidebarStore.takeId = row.takeId
        sidebarStore.openPanel = skill
        sidebarStore.openSelfEval = selfEval
        sidebarStore.openP360 = row.proctortype === ProctorTypes.P360.valueOf()
        sidebarStore.P360Data = row.examData
        sidebarStore.isPlaceC = row.product_package === "pl-c"
        testTakerSortableTableStore.selectedRow = row
        takeSearchStore.findSidebarInfoForTake(row.takeId)
    }
}

const TestTakerSortableTable = observer((props: TestTakerSortableTableProps) => {
    const [reportTableRows, setReportTableRows] = useState<TableDataRow[]>([])
    const [columns, setColumns] = useState<{left: Column<any>[], right: Column<any>[]}>({left: [], right:[]})
    const {reportProps} = props
    const {printOptions} = reportProps
    const tableRef = useRef<MaterialTable<TableDataRow>>(null)

    useEffect(() => {
        // Make sure that there are no ids leftover here on refresh
        reportsStore.selectedTakeIds = []

        const tableRows: TableDataRow[] = []
        const takeRows: ReportTakeRow[] = reportsStore.reportTableData!.reportTakeRows
        takeRows.forEach((row: ReportTakeRow, index: number) => {
            tableRows.push({
                ...row,
                tableData: {
                    id: index,
                    checked: false,
                },
            })
        })

        // Filter out scaled columns if there aren't any scores in the list
        if (!tableRows.some((row: TableDataRow) => row.readingScaledScore)) {
            const index: number = reportProps.skillColumns.map((col: SkillColumnData) => col.scaledScoreKey).indexOf("readingScaledScore")
            index >= 0 && reportProps.skillColumns.splice(index, 1)
        }
        if (!tableRows.some((row: TableDataRow) => row.listeningScaledScore)) {
            const index: number = reportProps.skillColumns.map((col: SkillColumnData) => col.scaledScoreKey).indexOf("listeningScaledScore")
            index >= 0 && reportProps.skillColumns.splice(index, 1)
        }
        const reportColumns = buildReportColumns(reportProps.skillColumns, typeof printOptions === "undefined" ? false : true)

        setReportTableRows(tableRows)
        setColumns(reportColumns)
    }, [])

    if (!columns || !reportTableRows) return <></>

    // This is called when the use clicks anywhere on a row (not including the sidebar links, individual report link, etc)
    const onRowClick = (event?: React.MouseEvent<Element, MouseEvent>, rowData?: TableDataRow) => {
        if (rowData) {
            updateRow(rowData)
        }
    }

    // This is called when the user clicks on the checkboxes
    const onSelectionChange = (data: TableDataRow[], row?: TableDataRow) => {
        if (row) {
            updateSelectedIds(row)
        } else if (data.length) {
            reportsStore.selectedTakeIds = []
            data.forEach((r: TableDataRow) => {
                updateSelectedIds(r)
            })
        } else {
            reportsStore.selectedTakeIds = []
        }

        refreshTable()
    }

    const updateRow = (rowData: TableDataRow) => {
        const row: TableDataRow = reportTableRows[rowData.tableData.id]
        row.tableData.checked = !row.tableData.checked
        setReportTableRows(reportTableRows)

        updateSelectedIds(rowData)
        refreshTable()
    }

    const updateSelectedIds = (rowData: TableDataRow) => {
        const selectedIds = [...reportsStore.selectedTakeIds]
        const takeIdIndex = selectedIds.indexOf(rowData.takeId)
        if (takeIdIndex === -1) {
            selectedIds.push(rowData.takeId)
        } else {
            selectedIds.splice(takeIdIndex, 1)
        }

        reportsStore.selectedTakeIds = selectedIds

        tableRef.current && tableRef.current.setState((prevState: TableDataRow[]) => {
            prevState[rowData.tableData.id] = rowData
        })
    }

    const refreshTable = () => {
        tableRef.current!.forceUpdate()
    }

    return (
        <MuiThemeProvider theme={theme}>
            <MaterialTable
                tableRef={tableRef}
                columns={[...columns.left, ...columns.right]}
                data={reportTableRows}
                onRowClick={onRowClick}
                onSelectionChange={onSelectionChange}
                options={{
                    search: false,
                    toolbar: false,
                    paging: false,
                    selection: typeof printOptions === "undefined",
                    draggable: false,
                    padding: "dense",
                    fixedColumns: {
                        left: columns.left.length,
                    },
                    tableLayout: "auto",
                    headerStyle: {
                        fontSize: "1.5rem",
                        height: 52,
                        whiteSpace: "nowrap",
                        zIndex: 0,
                    },
                    rowStyle: (data) => ({
                        fontSize: "1rem",
                        backgroundColor: (data.tableData.id % 2 === 0) ? AvantColors.REPORT_BACKGROUND_GRAY_DARK : "#FFF",
                    })

                }}
            />
        </MuiThemeProvider>
    )
})

export default TestTakerSortableTable
